import { formatQty } from "@/components/Shared";

// Pomocné funkce pro práci s jednotkami výrobků (ks + alternativní jednotky).

/**
 * Vrátí velikost balení pro výrobek (kolik ks v 1 balení).
 * Pozn.: legacy pole, používá se jen pokud výrobek nemá vlastní `units`.
 */
export function packageSize(product) {
  const n = Number(product?.package_size);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Vrátí název balení (např. "balení", "karton"). Default "balení".
 */
export function packageLabel(product) {
  return product?.package_label?.trim() || "balení";
}

/**
 * Vrátí počet ks v 1 jednotce daného režimu.
 * - "piece" → 1
 * - "package" (legacy) → product.package_size
 * - jiný label → ratio z product.units[]
 */
export function unitRatio(product, mode) {
  if (!mode || mode === "piece") return 1;
  if (mode === "package") return packageSize(product);
  const list = Array.isArray(product?.units) ? product.units : [];
  const match = list.find((u) => u && u.label === mode);
  const r = Number(match?.ratio);
  return Number.isFinite(r) && r > 0 ? r : 1;
}

/**
 * Vrátí dostupné jednotky výrobku jako pole {value, label, ratio}.
 * Vždy obsahuje "piece" (ks) a všechny custom alt jednotky.
 */
export function productUnitOptions(product) {
  const opts = [{ value: "piece", label: "ks", ratio: 1 }];
  const list = Array.isArray(product?.units) ? product.units : [];
  list.forEach((u) => {
    if (!u || !u.label || !Number(u.ratio)) return;
    opts.push({
      value: u.label,
      label: `${u.label} (po ${Number(u.ratio)} ks)`,
      ratio: Number(u.ratio),
    });
  });
  return opts;
}

/**
 * Z entered count (count v zvolené jednotce) + mode + výrobku vypočítá ks.
 */
export function toPieces(product, mode, count) {
  const n = parseFloat(count);
  if (!Number.isFinite(n)) return 0;
  return n * unitRatio(product, mode);
}

/**
 * Formátování záznamu, který je veden v ks ale původně byl zadán v jiné jednotce.
 */
export function formatUnitDisplay(product, record, unit = "ks") {
  if (!record) return "";
  const qty = Number(record.quantity ?? record.received_quantity ?? 0);
  const mode = record.unit_mode;
  if (mode && mode !== "piece" && record.unit_count) {
    const ratio = unitRatio(product, mode);
    if (ratio > 1) {
      const label = mode === "package" ? packageLabel(product) : mode;
      return `${record.unit_count} ${label} × ${ratio} = ${qty} ${unit}`;
    }
  }
  return `${qty} ${unit}`;
}

/**
 * Hlavní formátování: vrátí "24 ks · 2 balení" pokud má výrobek balení > 1,
 * jinak jen "24 ks".
 */
export function formatQtyWithPackage(qty, product, unit = "ks") {
  const n = Number(qty || 0);
  const base = formatQty(n, unit);
  const ps = packageSize(product);
  if (ps <= 1 || n <= 0) return base;
  const full = Math.floor(n / ps);
  const remainder = n - full * ps;
  const label = packageLabel(product);
  if (full === 0) return base;
  if (Math.abs(remainder) < 1e-9) {
    return `${base} · ${full} ${label}`;
  }
  return `${base} · ${full} ${label} + ${formatQty(remainder)} ${unit}`;
}
